import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAiChat, ChatMessages, SparkleIcon } from "./AiChatParts";
import "./AiAnalyst.css"; // shared message/chip/composer styles
import "./AiChatWidget.css";

/**
 * Floating AI Analyst widget — sparkle launcher bottom-right on every page.
 * Opens a compact chat panel; ⛶ expands it in both width and height.
 * Hidden on /ai-analyst (the full page already provides the chat there).
 */

const SUGGESTIONS = [
	"Why did DAU change today vs yesterday?",
	"Which workspace is most engaged this week?",
	"Which users are going inactive lately?",
];

const AiChatWidget = () => {
	const location = useLocation();
	const chat = useAiChat();
	const [isOpen, setIsOpen] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [input, setInput] = useState("");
	const bottomRef = useRef(null);
	const inputRef = useRef(null);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [chat.messages, chat.streamingText, chat.toolEvents]);

	useEffect(() => {
		if (isOpen) inputRef.current?.focus();
	}, [isOpen]);

	// The full page already hosts the chat there.
	if (location.pathname === "/ai-analyst") return null;

	const submit = (text) => {
		chat.send(text ?? input);
		setInput("");
	};

	return (
		<>
			{!isOpen && (
				<button
					className="ai-widget-fab"
					onClick={() => setIsOpen(true)}
					title="Ask AI Analyst"
					aria-label="Open AI Analyst chat"
				>
					<span className="ai-widget-fab-icon">
						<SparkleIcon size={19} />
					</span>
				</button>
			)}

			{isOpen && (
				<div className={`ai-widget-panel ${isExpanded ? "expanded" : ""}`}>
					<div className="ai-widget-head">
						<span className="ai-widget-head-icon">
							<SparkleIcon size={18} />
						</span>
						<div className="ai-widget-head-title">
							<b>AI Analyst</b>
							<small>● live data</small>
						</div>
						{chat.messages.length > 0 && (
							<button
								className="ai-widget-head-btn"
								onClick={chat.clear}
								disabled={chat.isLoading}
								title="New chat"
							>
								⟳
							</button>
						)}
						<button
							className="ai-widget-head-btn"
							onClick={() => setIsExpanded((v) => !v)}
							title={isExpanded ? "Collapse" : "Expand"}
						>
							{isExpanded ? "⛶" : "⛶"}
						</button>
						<button
							className="ai-widget-head-btn"
							onClick={() => {
								setIsOpen(false);
								setIsExpanded(false);
							}}
							title="Close"
						>
							✕
						</button>
					</div>

					<div className="ai-chat-scroll ai-widget-body">
						<ChatMessages
							chat={chat}
							suggestions={SUGGESTIONS}
							onSuggestion={submit}
							bottomRef={bottomRef}
							emptyState={
								<div className="ai-widget-empty">
									<SparkleIcon size={34} />
									<h2>Ask about your data</h2>
								</div>
							}
						/>
					</div>

					<div className="ai-composer ai-widget-composer">
						<textarea
							ref={inputRef}
							value={input}
							onChange={(e) => setInput(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									submit();
								}
							}}
							placeholder="Ask about your data…"
							rows={1}
							disabled={chat.isLoading}
						/>
						{chat.isLoading ? (
							<button className="ai-send-btn stop" onClick={chat.stop}>
								◼
							</button>
						) : (
							<button className="ai-send-btn" onClick={() => submit()} disabled={!input.trim()}>
								➤
							</button>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default AiChatWidget;
